import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import moment from 'moment/moment';

// project imports
import FormControl from '../Form/FormControl';
import FormControlRadio from '../Form/FormControlRadio';
import FormControlSelect from '../Form/FormControlSelect';
import FormControlDatePicker from 'components/forms/Form/FormControlDatePicker';
import {
  AppModule,
  DateFormat,
  GRID_SPACING,
  InputType
} from 'utils/constants';
import {
  createFormControlProps,
  createValidationSchema,
  getTranslationStringFromPath,
  hasTheSameKey,
  isEmptyObject
} from 'utils';
import MainCard from 'components/cards/MainCard';
import AnimateButton from 'components/assets/AnimateButton';
import { useAppSelector } from 'hooks/useAppStore';
import {
  selectEaiCodebooks,
  selectEaiServiceLevelSpecification
} from 'features/eai/selectors';

// assets
import { IconX } from '@tabler/icons';

// material-ui
import {
  Button,
  Grid,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';

// types
import { KeyedObject, RadioInputOption, WizardInputOption } from 'models';
import { FormWizardChild, FormWizardStep } from 'models/form';
import { selectAjaxLoader } from 'features/app/appSelectors';

interface WizardProps {
  module: AppModule;
  steps: FormWizardStep[];
  onSubmit: (state: KeyedObject) => void;
  initialState: KeyedObject;
  i18nWizardRoot: string;
  i18nEntityRoot: string;
}

const getWizardInputDate = (value: string): string => {
  return moment(value).format(DateFormat.DATE_FORMAT);
};

const getStepChildByInputName = (
  steps: FormWizardStep[],
  inputName: string
): FormWizardChild | undefined => {
  const allStepsChildren = steps.map((step) => step.children).flat();
  return allStepsChildren.find((child) => child.id === inputName);
};

const getInputValueTextForWizardReview = (
  inputName: string,
  inputValue: string,
  activeSteps: FormWizardStep[],
  module: AppModule
): string => {
  const stepChildInput = getStepChildByInputName(activeSteps, inputName);
  if (stepChildInput) {
    const { type } = stepChildInput;
    const options = stepChildInput.options as
      | (WizardInputOption & RadioInputOption)[]
      | undefined;

    switch (type) {
      case InputType.RADIO_GROUP:
      case InputType.SELECT: {
        const selectedOption = options?.find((op) => op.value === inputValue);
        const selectedOptionLabelString =
          typeof selectedOption?.label === 'string'
            ? selectedOption?.label
            : undefined;
        const selectedOptionLabel =
          selectedOption?.labelForWizardReview ?? selectedOptionLabelString;

        const title =
          selectedOptionLabel ??
          getTranslationStringFromPath(
            `module.${module}.codebooks.${inputValue}.title`
          );
        return title ?? inputValue;
      }
      case InputType.DATE:
        return getWizardInputDate(inputValue);
      case InputType.NUMBER:
      case InputType.TEXT_FIELD: {
        return inputValue;
      }
      default:
        return '';
    }
  }
  return '';
};

// ==============================|| WIZARD ||============================== //

const Wizard = ({
  module,
  steps,
  onSubmit,
  initialState,
  i18nWizardRoot,
  i18nEntityRoot
}: WizardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState<number>(0);
  const [activeSteps, setActiveSteps] = useState<FormWizardStep[]>([]);
  const [errorIndex, setErrorIndex] = useState<number | null>(null);
  const [isReviewPage, setIsReviewPage] = useState<boolean>(false);

  const operationInProgress = useAppSelector(selectAjaxLoader);

  const {
    getFieldProps,
    handleSubmit,
    values,
    errors,
    touched,
    setFieldValue,
    setTouched
  } = useFormik({
    initialValues: initialState,
    validationSchema: createValidationSchema(
      activeSteps[activeStep]?.children || []
    ),
    onSubmit: (newValues) => {
      if (isReviewPage) {
        onSubmit(newValues);
      }

      setActiveStep(isReviewPage ? activeStep : activeStep + 1);
      setErrorIndex(isReviewPage ? errorIndex : null);
    },
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: true
  });

  useEffect(() => {
    setActiveSteps(
      steps.reduce((acc: FormWizardStep[], step) => {
        if (step.renderConditions) {
          let shouldRender = true;

          for (const condition of step.renderConditions) {
            const { input, value } = condition;

            if (values[input] !== value) {
              shouldRender = false;
              break;
            }
          }

          if (shouldRender) acc.push(step);
          else {
            step.children.forEach((child) => {
              setFieldValue(child.id, '');
            });
          }
        } else {
          acc.push(step);
        }

        return acc;
      }, [])
    );

    setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, steps]);

  useEffect(() => {
    setErrorIndex(isEmptyObject(errors) ? null : activeStep);
  }, [activeStep, errors]);

  useEffect(() => {
    setIsReviewPage(activeStep === activeSteps.length - 1);
  }, [activeStep, activeSteps]);

  const handleNext = () => {
    handleSubmit();
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    setErrorIndex(null);
  };

  const wizardSteps = activeSteps.map((step, index) => {
    const labelProps: { error?: boolean; optional?: React.ReactNode } = {};
    if (index === errorIndex && hasTheSameKey(errors, touched)) {
      labelProps.error = true;
    }

    return (
      <Step key={index}>
        <StepLabel {...labelProps}>
          {t(
            index === activeSteps.length - 1
              ? step.label
              : `${i18nWizardRoot}.${step.label}`
          )}
        </StepLabel>
      </Step>
    );
  });

  const eaiCodebooks = useAppSelector(selectEaiCodebooks);
  const eaiServiceLevelSpecification = useAppSelector(
    selectEaiServiceLevelSpecification
  );

  const getControlProps = (
    name: string,
    type: InputType,
    serviceSpecification?: boolean,
    options?: WizardInputOption[] | RadioInputOption[],
    endAdornment?: string,
    maxLength?: number
  ) => {
    return {
      ...createFormControlProps({
        name,
        type,
        touched,
        errors,
        codebooks: eaiCodebooks,
        module,
        entityRoot: i18nEntityRoot,
        disabled: false,
        serviceLevelSpecification: serviceSpecification
          ? eaiServiceLevelSpecification
          : undefined,
        options,
        endAdornment,
        maxLength
      }),
      setFieldValue,
      ...getFieldProps(name)
    };
  };

  const wizardContent: ReactElement = (
    <Grid container spacing={GRID_SPACING} direction='column'>
      {activeSteps[activeStep]?.children.map((child, index) => {
        const FormControlProps = getControlProps(
          child.id,
          child.type,
          child.serviceSpecification,
          child.options,
          child.endAdornment,
          child.maxLength
        );
        let shouldRenderChild = true;

        if (child.renderConditions) {
          for (const condition of child.renderConditions) {
            const { input, value } = condition;

            if (values[input] !== value) {
              shouldRenderChild = false;
              break;
            }
          }
        }

        if (shouldRenderChild) {
          return (
            <Grid
              key={index}
              container
              alignItems='center'
              justifyContent='center'
              item
            >
              <Grid item xs={12} md={6}>
                {child.type === InputType.TEXT_FIELD ? (
                  <FormControl {...FormControlProps} />
                ) : child.type === InputType.RADIO_GROUP ? (
                  <FormControlRadio {...FormControlProps} />
                ) : child.type === InputType.SELECT ? (
                  <FormControlSelect {...FormControlProps} />
                ) : child.type === InputType.DATE ? (
                  <FormControlDatePicker {...FormControlProps} />
                ) : (
                  <>child</>
                )}
              </Grid>
            </Grid>
          );
        } else {
          return null;
        }
      })}
    </Grid>
  );

  const wizardReview: ReactElement = (
    <Grid container spacing={2} direction='column'>
      {Object.entries(values).reduce(
        (acc: React.ReactElement[], [inputName, inputValue]) => {
          if (inputValue) {
            acc.push(
              <Grid
                key={inputName}
                container
                alignItems='center'
                justifyContent='center'
                item
              >
                <Grid item xs={11} sm={5} lg={5} xl={4}>
                  <Typography variant='subtitle1' color='textPrimary'>
                    {t(`${i18nEntityRoot}.input.${inputName}`) + ': '}
                    <Typography
                      variant='body1'
                      color='textSecondary'
                      component='span'
                    >
                      {getInputValueTextForWizardReview(
                        inputName,
                        inputValue,
                        activeSteps,
                        module
                      )}
                    </Typography>
                  </Typography>
                </Grid>
              </Grid>
            );
          }

          return acc;
        },
        []
      )}
    </Grid>
  );

  return (
    <Grid container spacing={GRID_SPACING} justifyContent='center'>
      <Grid item xs={12} lg={9} xl={7}>
        <MainCard
          title={t(`${i18nWizardRoot}.title`)}
          secondary={
            <Button
              color={'inherit'}
              onClick={() => navigate(-1)}
              sx={{ my: 0, ml: 0 }}
              disabled={operationInProgress}
            >
              <IconX />
            </Button>
          }
        >
          <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
            {wizardSteps}
          </Stepper>

          {!isReviewPage ? wizardContent : wizardReview}

          <Stack direction='row' justifyContent='flex-end'>
            {activeStep !== 0 && (
              <Button
                onClick={handleBack}
                sx={{ my: 3, ml: 1 }}
                disabled={operationInProgress}
              >
                {t('common.button.back')}
              </Button>
            )}
            <AnimateButton>
              <Button
                variant='contained'
                onClick={handleNext}
                sx={{ my: 3, ml: 1 }}
                disabled={operationInProgress}
              >
                {isReviewPage
                  ? t(`${i18nWizardRoot}.save`)
                  : t('common.button.next')}
              </Button>
            </AnimateButton>
          </Stack>
        </MainCard>
      </Grid>
    </Grid>
  );
};

export default Wizard;
